// Types and utilities for the analytics fact table system.
// The fact table catalog is fetched from GET /api/analytics/analytics_queries/v2/fact_tables
// and provides columns, filters, dimensions, and custom metrics per fact table.

// --- Types ---

export interface ColumnMeta {
  name: string
  type: 'all' | 'boolean' | 'timestamp' | 'string' | 'numeric' | 'entity'
  operations: string[]
  label?: string
  knownValues?: string[]
  entityKind?: string
}

export interface CustomMetricMeta {
  id: string
  label?: string
}

export interface QuickMetricMeta {
  id: string
  label: string
  column?: string
  operation?: string
  value?: string
  customMetric?: string
}

export interface Filter {
  id: string
  columnName?: string
  kind: string
  label?: string
  entityKind?: string
  multi?: boolean
  clientOnly?: boolean
  options?: Array<{ value: string; label: string }>
}

export interface Dimension {
  id: string
  kind: string
  columnName?: string
  label?: string
  name?: string
  group?: string
  geo?: string
  expression?: string
  tableName?: string
  options?: Array<{ id: number; name: string }>
}

export interface FactTable {
  kind: string
  label: string
  columns: ColumnMeta[]
  customMetrics: CustomMetricMeta[]
  quickMetrics?: QuickMetricMeta[]
  filters: Filter[]
  dimensions: Dimension[]
  segments: string[]
}

// AI-generated raw SQL fact table — carried on a query when the LLM produces SQL
// that doesn't fit an existing fact table. Synthesized into a FactTable client-side
// via rawSqlFactTableToFactTable so the standard slot UI sees it as another option.
export interface RawSqlColumn {
  name: string
  type: 'numeric' | 'string' | 'timestamp' | 'boolean' | 'entity'
  entityKind?: string
  time?: boolean
  label?: string
}

export interface RawSqlFactTablePayload {
  sql: string
  columns: RawSqlColumn[]
}

const VALID_OPERATIONS_BY_TYPE: Record<RawSqlColumn['type'], string[]> = {
  boolean: ['count', 'count_true', 'count_false', 'count_where'],
  timestamp: ['count', 'count_non_null', 'count_null', 'min', 'max'],
  string: ['count', 'count_non_null', 'count_null', 'count_distinct', 'count_where'],
  numeric: ['count', 'count_non_null', 'count_null', 'count_where', 'sum', 'avg', 'min', 'max'],
  entity: ['count', 'count_non_null', 'count_null', 'count_distinct'],
}

/**
 * Build a synthetic FactTable from an AI-generated raw_sql_fact_table payload.
 * Used so the slot UI can render filter/dimension/metric pickers against the
 * pasted SQL exactly as it would for a native fact table.
 */
export function rawSqlFactTableToFactTable(payload: RawSqlFactTablePayload): FactTable {
  const columns: ColumnMeta[] = [
    { name: '*', type: 'all', operations: ['count'] },
    ...payload.columns.map(c => ({
      name: c.name,
      type: c.type,
      operations: VALID_OPERATIONS_BY_TYPE[c.type] || [],
      label: c.label,
      entityKind: c.entityKind,
    })),
  ]

  const filters: Filter[] = []
  const dimensions: Dimension[] = []
  for (const c of payload.columns) {
    if (c.type === 'entity' && c.entityKind) {
      filters.push({
        id: c.name,
        columnName: c.name,
        kind: 'entity',
        entityKind: c.entityKind,
        multi: true,
        clientOnly: true,
        label: c.label || c.name,
      })
    }
    if (c.time) {
      filters.push({ id: 'time', columnName: c.name, kind: 'datetime', label: c.label || c.name })
      dimensions.push({ id: 'time', kind: 'time', columnName: c.name, label: c.label || c.name })
    } else {
      // Every non-time column is a candidate group-by dimension.
      dimensions.push({
        id: c.name,
        kind: 'expression',
        expression: `base_table.${c.name}`,
        label: c.label || c.name,
      })
    }
  }

  // Tag filter — mirrors backend RawSqlFactTable.register_tag_filter; surfaces
  // when the raw SQL exposes a join column for a tag-able entity. Priority:
  // campaign > flow > target (base DSL only allows one tag_filter per table).
  const tagEntityPriority = ['campaign', 'flow', 'target']
  const tagEntity = tagEntityPriority.find(k =>
    payload.columns.some(c => c.type === 'entity' && c.entityKind === k)
  )
  if (tagEntity) {
    filters.push({
      id: 'tag_id',
      kind: 'tags',
      entityKind: 'tag',
      multi: true,
      clientOnly: true,
      label: 'Tags',
    })
  }

  return {
    kind: 'raw_sql',
    label: 'Custom SQL',
    columns,
    customMetrics: [],
    quickMetrics: [],
    filters,
    dimensions,
    segments: [],
  }
}

// Metric types — what the frontend sends to the API

export interface ColumnMetric {
  id: string
  factTable: string
  column: string
  operation: string
  value?: string
  label?: string
  cumulative?: boolean
}

export interface CustomMetricRef {
  id: string
  factTable: string
  customMetric: string
  label?: string
  cumulative?: boolean
}

export interface RatioMetric {
  id: string
  operation: 'ratio'
  numerator: string
  denominator: string
  label?: string
}

export type Metric = ColumnMetric | CustomMetricRef | RatioMetric

// Type guards

export function isRatioMetric(m: Metric): m is RatioMetric {
  return (m as RatioMetric).operation === 'ratio'
}

export function isCustomMetricRef(m: Metric): m is CustomMetricRef {
  return !!(m as CustomMetricRef).customMetric
}

export function isColumnMetric(m: Metric): m is ColumnMetric {
  return !isRatioMetric(m) && !isCustomMetricRef(m)
}

// --- Quick metric expansion ---

export function expandQuickMetric(qm: QuickMetricMeta, factTableKind: string): Metric {
  if (qm.customMetric) {
    return { id: '', factTable: factTableKind, customMetric: qm.customMetric, label: qm.label } as CustomMetricRef
  }
  const metric: ColumnMetric = { id: '', factTable: factTableKind, column: qm.column!, operation: qm.operation!, label: qm.label }
  if (qm.value) metric.value = qm.value
  return metric
}

// --- Operation labels ---

export const OPERATION_LABELS: Record<string, string> = {
  count: 'Count',
  count_non_null: 'Count Non-Null',
  count_null: 'Count Null',
  count_distinct: 'Count Distinct',
  count_true: 'Count True',
  count_false: 'Count False',
  count_where: 'Count Where',
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
}

// --- Utility functions ---

/**
 * Extract unique fact table kinds referenced by an array of metrics.
 * Ratio metrics are skipped since they reference other metrics, not fact tables.
 */
export function referencedFactTableKinds(metrics: Metric[]): string[] {
  const kinds = new Set<string>()
  for (const m of metrics) {
    if (!isRatioMetric(m) && (m as ColumnMetric | CustomMetricRef).factTable) {
      kinds.add((m as ColumnMetric | CustomMetricRef).factTable)
    }
  }
  return [...kinds]
}

/**
 * Compute available dimensions as the intersection across all referenced fact tables.
 * A dimension is available only if every referenced fact table supports it (matched by id).
 * When no fact tables are referenced, returns an empty array.
 */
export function computeAvailableDimensions(
  factTables: FactTable[],
  referencedKinds: string[]
): Dimension[] {
  if (referencedKinds.length === 0) return []

  const dimSets = referencedKinds.map(kind => {
    const ft = factTables.find(f => f.kind === kind)
    return ft?.dimensions || []
  })

  // Intersection: keep dimensions whose id appears in ALL referenced tables
  const first = dimSets[0]
  return first.filter(dim =>
    dimSets.every(set => set.some(d => d.id === dim.id))
  )
}

/**
 * Compute available filters as the union across all referenced fact tables.
 * Deduplicated by filter id + kind. Filters with clientOnly=true are excluded
 * when singleClient is false.
 */
export function computeAvailableFilters(
  factTables: FactTable[],
  referencedKinds: string[],
  singleClient: boolean
): Filter[] {
  const seen = new Set<string>()
  const result: Filter[] = []

  for (const kind of referencedKinds) {
    const ft = factTables.find(f => f.kind === kind)
    for (const filter of ft?.filters || []) {
      if (filter.clientOnly && !singleClient) continue
      const key = `${filter.id}-${filter.kind}`
      if (!seen.has(key)) {
        seen.add(key)
        result.push(filter)
      }
    }
  }

  return result
}

/**
 * Generate a unique metric id that doesn't collide with existing metrics.
 * Produces m1, m2, m3, ...
 */
export function generateMetricId(existingMetrics: Metric[]): string {
  const usedNumbers = new Set<number>()
  for (const m of existingMetrics) {
    const match = m.id.match(/^m(\d+)$/)
    if (match) usedNumbers.add(parseInt(match[1], 10))
  }
  let n = 1
  while (usedNumbers.has(n)) n++
  return `m${n}`
}

/**
 * Generate a human-readable label for a metric based on its components and fact table metadata.
 */
export function metricDisplayLabel(metric: Metric, factTables: FactTable[]): string {
  if (metric.label) return metric.label

  if (isRatioMetric(metric)) {
    return `${metric.numerator} / ${metric.denominator}`
  }

  const ft = factTables.find(f => f.kind === (metric as ColumnMetric | CustomMetricRef).factTable)
  const ftLabel = ft?.label || (metric as ColumnMetric | CustomMetricRef).factTable

  if (isCustomMetricRef(metric)) {
    const cm = ft?.customMetrics?.find(c => c.id === metric.customMetric)
    return cm?.label ? `${ftLabel} — ${cm.label}` : `${ftLabel} — ${metric.customMetric}`
  }

  const col = metric as ColumnMetric
  const colMeta = ft?.columns?.find(c => c.name === col.column)
  const colLabel = colMeta?.label || col.column
  const opLabel = OPERATION_LABELS[col.operation] || col.operation

  if (col.column === '*') return `${ftLabel} — ${opLabel}`
  if (col.operation === 'count_where' && col.value) {
    return `${ftLabel} — ${colLabel} = ${col.value}`
  }
  return `${ftLabel} — ${opLabel}(${colLabel})`
}
